import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { Button, Message, Ref } from 'semantic-ui-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLoginWithEmail } from 'services/auth/login-email/hook';
import { ShouldRender } from 'components/ShouldRender';
import classNames from 'classnames';
import { AppContext } from 'AppContext';
import { useRedirect } from 'utils/redirect';
import { Link, useLocation } from 'react-router-dom';
import { PasswordInput } from 'components/form-elements/inputs/PasswordInput';
import { TENANT_ID } from 'global/environment';
import { Auth } from 'aws-amplify';
import styles from '../../styles.module.scss';
import { PASSWORD_PATTERN } from '../../../../../../../../../utils/patterns';

export const PasswordLogin = ({ email, emailError, setEmailError }) => {
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [isCompletingPassword, setIsCompletingPassword] = useState(false);

  const intl = useIntl();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const redirect = query.get('redirect_url');

  const { user, loading, error, requiresNewPassword, challengeUser, onEmailLogin } = useLoginWithEmail();

  const { setUser, setIsAuthenticated, setTenant } = useContext(AppContext);

  const { onRedirect } = useRedirect(redirect || '/');

  const submitRef = useRef(null);

  useEffect(() => {
    if (user) {
      setUser(user);
      setTenant(JSON.parse(user.attributes['custom:data'])[TENANT_ID]?.tenant);
      setIsAuthenticated(true);
      onRedirect();
    }
  }, [user, setUser, setIsAuthenticated, setTenant, onRedirect]);

  const onChangePassword = useCallback((e) => {
    setPassword(e.target.value);
    setPasswordError('');
  }, []);

  const onChangeNewPassword = useCallback((e) => {
    setNewPassword(e.target.value);
    setNewPasswordError('');
  }, []);

  const onChangeConfirmPassword = useCallback((e) => {
    setConfirmPassword(e.target.value);
    setConfirmPasswordError('');
  }, []);

  const onLogin = useCallback(() => {
    const passwordError = !password.trim().length;
    const emailError = !email.trim().length;

    if (passwordError && emailError) {
      setPasswordError('fieldRequired');
      setEmailError('fieldsRequired');
    } else if (passwordError) {
      setPasswordError('fieldRequired');
    } else if (emailError) {
      setEmailError('fieldRequired');
    } else {
      onEmailLogin(email, password);
    }
  }, [password, email, setPasswordError, setEmailError, onEmailLogin]);

  const onCompleteNewPassword = useCallback(async () => {
    const newPassError = !newPassword.trim().length;
    const confirmPassError = !confirmPassword.trim().length;
    const newPassPatternError = !PASSWORD_PATTERN.test(newPassword);
    const confirmPassPatternError = !PASSWORD_PATTERN.test(confirmPassword);
    const passwordsDoNotMatch = newPassword !== confirmPassword;

    switch (true) {
      case newPassError && confirmPassError:
        setNewPasswordError('fieldRequired');
        setConfirmPasswordError('fieldRequired');
        return;

      case newPassError:
        setNewPasswordError('fieldRequired');
        return;

      case confirmPassError:
        setConfirmPasswordError('fieldRequired');
        return;

      case newPassPatternError:
        setNewPasswordError('passPatternError');
        return;

      case confirmPassPatternError:
        setConfirmPasswordError('passPatternError');
        return;

      case passwordsDoNotMatch:
        setConfirmPasswordError('passMustMatch');
        return;

      default:
        break;
    }

    if (!challengeUser) {
      setNewPasswordError('genericErrorMessage');
      return;
    }

    try {
      setIsCompletingPassword(true);

      // Complete the NEW_PASSWORD_REQUIRED challenge
      const updatedUser = await Auth.completeNewPassword(
        challengeUser,
        newPassword,
      );

      setIsCompletingPassword(false);

      setUser(updatedUser);
      setTenant(JSON.parse(updatedUser.challengeParam.userAttributes['custom:data'])[TENANT_ID]?.tenant);
      setIsAuthenticated(true);
      onRedirect();

    } catch (error) {
      setIsCompletingPassword(false);
      setNewPasswordError('genericErrorMessage');
    }
  }, [newPassword, confirmPassword, challengeUser, setUser, setTenant, setIsAuthenticated, onRedirect]);

  // Show new password form if required
  if (requiresNewPassword && challengeUser) {
    return (
      <>
        <Message
          info
          className={styles.field}
        >
          <FormattedMessage id="newPasswordRequiredMessage" />
        </Message>

        <PasswordInput
          fluid
          className={classNames(styles.field, newPasswordError && 'error')}
          id="new-password"
          name="new-password"
          label={intl.formatMessage({ id: 'newPassword' })}
          placeholder={intl.formatMessage({ id: 'newPasswordPlaceholder' })}
          value={newPassword}
          onChange={onChangeNewPassword}
          submitRef={submitRef}
        />

        <PasswordInput
          fluid
          className={classNames(styles.field, confirmPasswordError && 'error')}
          id="confirm-password"
          name="confirm-password"
          label={intl.formatMessage({ id: 'confirmPassword' })}
          placeholder={intl.formatMessage({ id: 'confirmPassword' })}
          value={confirmPassword}
          onChange={onChangeConfirmPassword}
          submitRef={submitRef}
        />

        <div className={styles.actions}>
          <Ref innerRef={submitRef}>
            <Button
              className={styles.login}
              loading={loading || isCompletingPassword}
              disabled={loading || isCompletingPassword}
              onClick={onCompleteNewPassword}
            >
              <FormattedMessage id="setNewPassword" />
            </Button>
          </Ref>
        </div>

        <ShouldRender condition={!!newPasswordError || !!confirmPasswordError}>
          <Message
            negative
            className={styles.field}
          >
            <FormattedMessage id={newPasswordError || confirmPasswordError} />
          </Message>
        </ShouldRender>
      </>
    );
  }

  // Show normal login form
  return (
    <>
      <PasswordInput
        fluid
        className={classNames(styles.field, passwordError && 'error')}
        id="password"
        name="password"
        label={intl.formatMessage({ id: 'password' })}
        placeholder={intl.formatMessage({ id: 'passwordPlaceholder' })}
        value={password}
        onChange={onChangePassword}
        submitRef={submitRef}
      />

      <div className={styles.actions}>
        <Ref innerRef={submitRef}>
          <Button
            className={styles.login}
            loading={loading}
            disabled={loading}
            onClick={onLogin}
          >
            <FormattedMessage id="login" />
          </Button>
        </Ref>

        <Link to="/forgot-pass">
          <FormattedMessage id="forgotPassword" />
        </Link>
      </div>

      <ShouldRender condition={(!!passwordError || !!error) && !emailError}>
        <Message
          negative
          className={styles.field}
        >
          <FormattedMessage id={passwordError || error} />
        </Message>
      </ShouldRender>
    </>
  );
};
